import { ApplicationState } from '..';
import { ExtraItem, ExtraData, PackSettings, PaymentProfile, InvoiceCatalogData, InvoicesProvider, TaxRate, TaxExemption, DocumentSettings } from '../../models';
import { SettingsData } from '../../models';
import { GeneralSettingsData } from '../../models';
import { CatalogCategoryData, ReportTemplateData } from '../../models/api-models';
import { SurveySettingsData } from '../../shared/surveys';


export const getEntitiesPageSize = (state: ApplicationState) => state.settings.entities.pageSize;

export const getSettings = (state: ApplicationState): SettingsData => state.settings;
export const getClientsLoginSettings = (state: ApplicationState) => state.settings.clientsLogin;
export const getGeneralSettings = (state: ApplicationState): GeneralSettingsData => state.settings.general;
export const getDocumentsSettings = (state: ApplicationState): DocumentSettings[] => state.settings.reports.documents ?? [];
export const getPaymentsSettings = (state: ApplicationState): PaymentProfile[] => state.settings.payments.profiles ?? [];
export const getExtrasSettings = (state: ApplicationState): ExtraData[] => state.settings.extras.extras ?? [];
export const getInvoiceCatalog = (state: ApplicationState): InvoiceCatalogData | undefined => state.settings.invoiceCatalog;
export const isInvoiceCatalogLoading = (state: ApplicationState): boolean => state.settings.invoiceCatalogLoading;
export const getInvoicesProvider = (state: ApplicationState): InvoicesProvider | undefined => state.settings.invoices?.provider;
export const getInvoiceSettings = (state: ApplicationState) => state.settings.invoices;
export const getTaxRates = (state: ApplicationState): TaxRate[] | undefined => state.settings.taxRates;
export const isTaxRatesLoading = (state: ApplicationState): boolean => state.settings.taxRatesLoading;
export const getTaxExemptions = (state: ApplicationState): TaxExemption[] | undefined => state.settings.taxExemptions;
export const isTaxExemptionsLoading = (state: ApplicationState): boolean => state.settings.taxExemptionsLoading;
export const getReportTemplates = (state: ApplicationState): ReportTemplateData[] | undefined => state.settings.reportTemplates;
export const isReportTemplatesLoading = (state: ApplicationState): boolean => state.settings.reportTemplatesLoading;
export const getReportTemplatesFetchedAt = (state: ApplicationState): number | undefined => state.settings.reportTemplatesFetchedAt;
export const getSurveys = (state: ApplicationState): SurveySettingsData[] | undefined => state.settings.surveys;
export const isSurveysLoading = (state: ApplicationState): boolean => state.settings.surveysLoading;

export const getLatestReportTemplates = (state: ApplicationState): ReportTemplateData[] | undefined => {
    const templates = state.settings.reportTemplates;
    if (!Array.isArray(templates)) return undefined;
    const byTemplateId = new Map<string, ReportTemplateData>();
    for (const t of templates) {
        const current = byTemplateId.get(t.templateId);
        if (!current || t.version > current.version) {
            byTemplateId.set(t.templateId, t);
        }
    }
    return Array.from(byTemplateId.values());
};

export const getReportTemplateCatalog = (state: ApplicationState): CatalogCategoryData[] | undefined => state.settings.reportTemplateCatalog;
export const isReportTemplateCatalogLoading = (state: ApplicationState): boolean => state.settings.reportTemplateCatalogLoading;

export const getPackTemplates = (state: ApplicationState): PackSettings[] | undefined => state.settings.packTemplates;
export const isPackTemplatesLoading = (state: ApplicationState): boolean => state.settings.packTemplatesLoading;
export const getPackTemplatesFetchedAt = (state: ApplicationState): number | undefined => state.settings.packTemplatesFetchedAt;

export const getLatestPackTemplates = (state: ApplicationState): PackSettings[] | undefined => {
    const templates = state.settings.packTemplates;
    if (!Array.isArray(templates)) return undefined;
    const byTemplateId = new Map<string, PackSettings>();
    for (const t of templates) {
        if (!t.templateId) continue;
        const current = byTemplateId.get(t.templateId);
        if (!current || t.version > current.version) {
            byTemplateId.set(t.templateId, t);
        }
    }
    return Array.from(byTemplateId.values());
};

export const getExpensesAvailableForAppoinementType = (state: ApplicationState, appointmentType: string | string[]): ExtraItem[] => {
    const types = Array.isArray(appointmentType) ? appointmentType : [appointmentType];
    return [
        ...(state.settings.extras.extras
            ?.filter(e => !e.appointmentTypes.length || types.some(type => e.appointmentTypes.includes(type)))
            .map(({id, name, priceForClient}) => ({id, name, value: priceForClient, quantity: 1})) ?? []),
    ];
}

export const getCompanyLogo = (state: ApplicationState): string|undefined => state.settings.general.companyImageUrl;
export const getCompanyName = (state: ApplicationState): string|undefined => state.settings.general.companyName;

export const areSettingsLoaded = (state: ApplicationState): boolean => {
    // Settings are loaded if appVersion is not the initial 'unkown' value
    return state.settings.appVersion !== 'unkown' && state.settings.appVersion !== '<login again to refresh>';
};
